import { useEffect, useRef, useState } from "react";
import { motion, useTime, useTransform, useInView } from "framer-motion";

const layer = "absolute inset-0 w-full h-full object-contain pointer-events-none select-none";

export const CRAWL_T = 14;
// shared timeline (seconds, 0..14): rest 0-4 | crawl out 4-7 | hidden 7-11.4 | crawl in 11.4-13.8 | rest 13.8-14
// clock holds at t=2 (centre rest) until the section scrolls into view, then runs (2s first pause)
export const useCrawlT = (sectionRef) => {
  const time = useTime();
  const startRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: "-25% 0px -25% 0px" });
  return useTransform(time, (ms) => {
    if (!inView) return 2;
    if (startRef.current === null) startRef.current = ms;
    return ((ms - startRef.current) / 1000 + 2) % CRAWL_T;
  });
};

export const easeInOut = (p) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);
export const easeIn = (p) => p * p;
export const easeOut = (p) => 1 - Math.pow(1 - p, 2);

const STEP = 0.8;
const gaitPhase = (s) => {
  if (s > 4 && s < 7) return s - 4;
  if (s > 11.4 && s < 13.8) return s - 11.4;
  return null;
};
const bobOf = (s) => {
  const p = gaitPhase(s);
  return p === null ? 0 : -9 * Math.abs(Math.sin((Math.PI * p) / STEP));
};
const swingOf = (s, amp, phase = 0) => {
  const p = gaitPhase(s);
  return p === null ? 0 : amp * Math.sin((2 * Math.PI * p) / (2 * STEP) + phase);
};

const jab = {
  duration: 1.6,
  times: [0, 0.12, 0.24, 0.36, 0.48, 0.6, 0.72, 1],
  repeat: Infinity,
  repeatDelay: 0.7,
  ease: "easeInOut",
};

const Limb = ({ src, rotate, origin }) => (
  <motion.img
    src={src}
    alt=""
    draggable={false}
    className={layer}
    style={{ rotate, transformOrigin: origin }}
  />
);

export const PantherSpotter = ({ className = "", t }) => {
  const ref = useRef(null);
  const [m, setM] = useState(null);

  useEffect(() => {
    const el = ref.current;
    const measure = () => {
      let node = el;
      let left = 0;
      while (node) {
        left += node.offsetLeft;
        node = node.offsetParent;
      }
      const w = el.offsetWidth;
      const vw = window.innerWidth;
      setM({ centre: vw / 2 - (left + w / 2), outR: vw - left + 60, outL: -(left + w + 60) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const x = useTransform(t, (s) => {
    if (!m) return 0;
    if (s < 4) return m.centre;
    if (s < 7) return m.centre + (m.outR - m.centre) * easeInOut((s - 4) / 3);
    if (s < 11.4) return m.outL;
    if (s < 13.8) return m.outL + (m.centre - m.outL) * easeInOut((s - 11.4) / 2.4);
    return m.centre;
  });
  const opacity = useTransform(t, (s) => (!m || (s >= 7 && s <= 11.4) ? 0 : 1));
  const y = useTransform(t, bobOf);
  const rotate = useTransform(t, (s) => -bobOf(s) / 7.5);
  const rearfoot = useTransform(t, (s) => swingOf(s, 3, Math.PI));
  const thigh = useTransform(t, (s) => swingOf(s, -4));
  const supparm = useTransform(t, (s) => swingOf(s, 5.5));

  return (
    <motion.div ref={ref} data-testid="panther-spotter" className={className} style={{ x, y, rotate, opacity, transformOrigin: "50% 100%" }}>
      <img src="/mascot/panther-body.webp" alt="TPECO leak detective crawling on the trail of a leak" className={layer} draggable={false} />
      <Limb src="/mascot/panther-rearfoot.webp" rotate={rearfoot} origin="27.1% 89.8%" />
      <Limb src="/mascot/panther-thigh.webp" rotate={thigh} origin="36.5% 61.1%" />
      <Limb src="/mascot/panther-supparm.webp" rotate={supparm} origin="61.4% 60.9%" />
      <motion.img
        src="/mascot/panther-head.webp"
        alt=""
        draggable={false}
        className={layer}
        animate={{ rotate: [0, -1.5, 0, -1.5, 0, -1.5, 0, 0] }}
        transition={jab}
        style={{ transformOrigin: "70.8% 55.5%" }}
      />
      <motion.img
        src="/mascot/panther-arm.webp"
        alt=""
        draggable={false}
        className={layer}
        animate={{ rotate: [0, -11, -4, -11, -4, -11, -4, 0] }}
        transition={jab}
        style={{ transformOrigin: "81.1% 54.6%" }}
      />
    </motion.div>
  );
};
